import { parseJsonBody, send } from './saveHttp';
import type {
    CreateRegistrationRequest,
    CreateRegistrationResponse,
    RegistrationApprovalResponse,
    RegistrationDetailResponse,
    RegistrationRenameResponse,
    SetApprovalRequest,
} from './connector-next-schemas';

/**
 * Hand-written client for the connector-next registration save endpoints.
 * HTTP conventions (If-Match, 409/428 mapping, PHI-safe problem parsing)
 * live in `saveHttp.ts`, shared with the structure-set save client.
 *
 * Matrix values are 16 row-major doubles of the DICOM item matrix
 * (moving → fixed frame, translation in mm) — the caller converts from
 * gl-matrix column-major at this boundary.
 */

export async function postRegistration(
    request: CreateRegistrationRequest
): Promise<CreateRegistrationResponse> {
    // One attempt (the unguarded-POST default): the connector mints new
    // UIDs for every create, so a replay after a lost response would
    // duplicate the registration.
    const response = await send('/api/registrations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
    });
    return parseJsonBody<CreateRegistrationResponse>(response);
}

export async function putRegistrationMatrix(
    id: string,
    ifMatch: string,
    matrix: readonly number[]
): Promise<RegistrationDetailResponse> {
    const response = await send(
        `/api/registrations/${id}/matrix`,
        {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ matrix }),
        },
        ifMatch
    );
    return parseJsonBody<RegistrationDetailResponse>(response);
}

export async function postRegistrationApproval(
    id: string,
    ifMatch: string,
    request: SetApprovalRequest
): Promise<RegistrationApprovalResponse> {
    const response = await send(
        `/api/registrations/${id}/approval`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(request),
        },
        ifMatch
    );
    return parseJsonBody<RegistrationApprovalResponse>(response);
}

export async function patchRegistrationLabel(
    id: string,
    ifMatch: string,
    label: string
): Promise<RegistrationRenameResponse> {
    const response = await send(
        `/api/registrations/${id}`,
        {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ label }),
        },
        ifMatch
    );
    return parseJsonBody<RegistrationRenameResponse>(response);
}

export async function deleteRegistration(id: string): Promise<void> {
    await send(`/api/registrations/${id}`, { method: 'DELETE' });
}

export async function getRegistrationDetail(
    id: string
): Promise<RegistrationDetailResponse> {
    const response = await send(`/api/registrations/${id}`, { method: 'GET' });
    return parseJsonBody<RegistrationDetailResponse>(response);
}
